import React, { useCallback } from 'react'

import {
  AlignItems,
  Body1,
  Box,
  Color,
  Display,
  FlexDirection,
  Heading3,
  Icon,
  Image,
  JustifyContent,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import * as appsConfig from '../../../config/apps'
import { useWelcomeTracking } from '../hooks/useWelcomeTracking'
import AppleStoreIcon from './assets/apple-store-icon.png'
import GooglePlayIcon from './assets/google-play-icon.png'

const AppBox = withCitrusTheme(Box, () => ({
  cursor: 'pointer',
}))

const AppPromo = () => {
  const { trackClickOnAppDownloadCTA } = useWelcomeTracking()

  const playStoreLink = `https://play.google.com/store/apps/details?id=${appsConfig.playStoreId}&utm_source=global_co&utm_medium=prtnr&utm_content=Mar2515&utm_campaign=PartBadge&pcampaignid=MKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1`
  const appStoreLink = `https://geo.itunes.apple.com/gb/app/gousto-uks-no1-recipe-kit/id${appsConfig.appStoreId}?mt=8`

  const clickHandler = useCallback(
    (redirectTo: string) => () => {
      trackClickOnAppDownloadCTA()
      window.location.assign(redirectTo)
    },
    [trackClickOnAppDownloadCTA],
  )

  return (
    <Box
      data-testid="appPromo"
      width="100%"
      gap={3}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
    >
      <Heading3 size={7}>Download the Gousto app</Heading3>
      <Box display={Display.Flex} gap={3} flexDirection={FlexDirection.Column} paddingV={[0, 0.5]}>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          gap={3}
          alignItems={AlignItems.Center}
        >
          <Icon name="frequency" />
          <Body1 size={4} color={Color.ColdGrey_800}>
            Access updates wherever you are
          </Body1>
        </Box>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          gap={3}
          alignItems={AlignItems.Center}
        >
          <Icon name="delivery" />
          <Body1 size={4} color={Color.ColdGrey_800}>
            Track and manage orders easily
          </Body1>
        </Box>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          gap={3}
          alignItems={AlignItems.Center}
        >
          <Icon name="use_by" />
          <Body1 size={4} color={Color.ColdGrey_800}>
            Never miss an order cut-off
          </Body1>
        </Box>
      </Box>
      <Box>
        <Box
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
          gap={4}
          alignItems={AlignItems.Center}
        >
          <Box
            display={Display.Flex}
            gap={[0, 0.5]}
            paddingV={[0, 2]}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
          >
            <Icon name="rate_full" size={8} />
            <Icon name="rate_full" size={8} />
            <Icon name="rate_full" size={8} />
            <Icon name="rate_full" size={8} />
            <Icon name="rate_half" size={8} />
          </Box>
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            alignItems={AlignItems.Center}
          >
            <Body1 color={Color.ColdGrey_800}>186K+ ratings</Body1>
          </Box>
        </Box>
        <Space size={4} />
        <Box
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
          alignItems={AlignItems.Center}
          gap={4}
        >
          <AppBox
            role="button"
            aria-label="Download the app on Play Store"
            onClick={clickHandler(playStoreLink)}
          >
            <Image
              src={GooglePlayIcon}
              alt="Google Play Download App Button"
              width={['140px', '160px', '160px', '160px']}
              height={['47px', '53px', '53px', '53px']}
            />
          </AppBox>
          <AppBox
            role="button"
            aria-label="Download the app on App Store"
            onClick={clickHandler(appStoreLink)}
          >
            <Image
              src={AppleStoreIcon}
              alt="Apple Store Download App Button"
              width={['140px', '160px', '160px', '160px']}
              height={['47px', '53px', '53px', '53px']}
            />
          </AppBox>
        </Box>
      </Box>
    </Box>
  )
}

export { AppPromo }
